import { useMemo, useState } from "react";
import { useFormState } from "react-hook-form";
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import AsyncCombobox, {
  ComboboxOption,
} from "@/components/inventory/AsyncCombobox";
import Modal from "@/components/Modal";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCrudExpense } from "@/hooks/expenses/useCrudExpense";
import { usePermissions } from "@/hooks/usePermissions";
import { useRestaurantCurrency } from "@/lib/hooks";
import { useGetExpenseCategoriesQuery } from "@/redux/services/expense/expense.api";
import {
  EXPENSE_PAYMENT_METHODS,
  getExpenseCategoryLabel,
} from "@/lib/expenses";
import PayeeAutocomplete from "./PayeeAutocomplete";

interface ExpenseFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  restaurantId: string;
}

const inputClass =
  "w-full border border-border rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent";

const labelClass = "block text-sm font-medium text-card-foreground mb-1";

const today = () => new Date().toISOString().slice(0, 10);

const ExpenseFormModal = ({
  isOpen,
  onClose,
  restaurantId,
}: ExpenseFormModalProps) => {
  const { register, control, watch, setValue, onSubmit, isLoading, noOpenSession } =
    useCrudExpense(onClose, restaurantId);
  // Subscribe to validation errors in the component itself so they re-render
  // reliably (the proxy subscription doesn't propagate through the hook).
  const { errors } = useFormState({ control });
  const { can } = usePermissions();
  const currency = useRestaurantCurrency();

  // Match the symbol shown elsewhere (e.g. "F CFA" for XOF) rather than the
  // raw ISO code, so the amount field reads like the rest of the app.
  const currencySymbol = useMemo(() => {
    try {
      const parts = new Intl.NumberFormat("fr-FR", {
        style: "currency",
        currency,
        minimumFractionDigits: 0,
      }).formatToParts(0);
      return parts.find((p) => p.type === "currency")?.value ?? currency;
    } catch {
      return currency;
    }
  }, [currency]);

  const { data: categories = [] } = useGetExpenseCategoriesQuery({
    restaurantId,
  });

  const [categorySearch, setCategorySearch] = useState("");
  const categoryOptions: ComboboxOption[] = useMemo(
    () =>
      categories
        .filter((c) => !c.archived)
        .map((c) => ({
          value: `/api/expense_categories/${c.id}`,
          label: getExpenseCategoryLabel(c),
        })),
    [categories],
  );
  const filteredCategoryOptions = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    return q
      ? categoryOptions.filter((o) => o.label.toLowerCase().includes(q))
      : categoryOptions;
  }, [categoryOptions, categorySearch]);

  const categoryValue = watch("category") ?? "";
  // Keep the trigger label correct even while the list is filtered down.
  const selectedCategoryLabel = categoryOptions.find(
    (o) => o.value === categoryValue,
  )?.label;
  const methodValue = watch("paymentMethod") ?? "";
  const payeeValue = watch("payee") ?? "";
  const paidFromDrawer = watch("paidFromDrawer") ?? false;

  // The drawer option is only meaningful for cash, and only for a user allowed
  // to touch the reconciled drawer.
  const showDrawerOption =
    methodValue === "cash" && can("cash_register.movement");

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Enregistrer une dépense"
      size="large"
    >
      <form onSubmit={onSubmit} className="space-y-5">
        {/* Amount + date — the core "how much / when" of the transaction. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label htmlFor="expense-amount" className={labelClass}>
              Montant <span className="text-danger">*</span>
            </label>
            <div className="relative">
              <input
                id="expense-amount"
                type="text"
                inputMode="decimal"
                autoFocus
                {...register("amount")}
                className={`${inputClass} pr-16 text-right tabular-nums`}
                placeholder="0"
              />
              <span className="pointer-events-none absolute inset-y-0 right-3 flex items-center text-sm text-muted-foreground">
                {currencySymbol}
              </span>
            </div>
            {errors.amount && (
              <p className="mt-1 text-xs text-danger">
                {errors.amount.message}
              </p>
            )}
          </div>

          <div>
            <label htmlFor="expense-date" className={labelClass}>
              Date <span className="text-danger">*</span>
            </label>
            <input
              id="expense-date"
              type="date"
              max={today()}
              {...register("incurredAt")}
              className={inputClass}
            />
            {errors.incurredAt && (
              <p className="mt-1 text-xs text-danger">
                {errors.incurredAt.message}
              </p>
            )}
          </div>
        </div>

        {/* Classification — what it was for and how it was paid. */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={labelClass}>
              Catégorie <span className="text-danger">*</span>
            </label>
            <AsyncCombobox
              value={categoryValue}
              selectedLabel={selectedCategoryLabel}
              onChange={(v) =>
                setValue("category", v, { shouldValidate: true })
              }
              options={filteredCategoryOptions}
              onSearchChange={setCategorySearch}
              placeholder="Sélectionner..."
              searchPlaceholder="Rechercher une catégorie..."
              emptyMessage="Aucune catégorie"
            />
            {errors.category && (
              <p className="mt-1 text-xs text-danger">
                {errors.category.message}
              </p>
            )}
          </div>

          <div>
            <label className={labelClass}>
              Moyen de paiement <span className="text-danger">*</span>
            </label>
            <Select
              value={methodValue}
              onValueChange={(v) => {
                setValue("paymentMethod", v as never, { shouldValidate: true });
                if (v !== "cash") setValue("paidFromDrawer", false);
              }}
            >
              <SelectTrigger>
                <SelectValue placeholder="Sélectionner..." />
              </SelectTrigger>
              <SelectContent>
                {EXPENSE_PAYMENT_METHODS.map((m) => (
                  <SelectItem key={m.value} value={m.value}>
                    {m.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {errors.paymentMethod && (
              <p className="mt-1 text-xs text-danger">
                {errors.paymentMethod.message}
              </p>
            )}
          </div>
        </div>

        {/* Beneficiary — supplier autocomplete, free text allowed. */}
        <div>
          <label htmlFor="expense-payee" className={labelClass}>
            Bénéficiaire
          </label>
          <PayeeAutocomplete
            id="expense-payee"
            value={payeeValue}
            onChange={(v) => setValue("payee", v)}
            restaurantId={restaurantId}
            placeholder="Fournisseur, employé, bailleur..."
          />
          <p className="mt-1 text-xs text-muted-foreground">
            Choisissez un fournisseur suggéré ou saisissez librement un autre
            bénéficiaire.
          </p>
        </div>

        <div>
          <label htmlFor="expense-description" className={labelClass}>
            Description
          </label>
          <textarea
            id="expense-description"
            {...register("description")}
            rows={3}
            className={`${inputClass} resize-none`}
            placeholder="Détails de la dépense..."
          />
        </div>

        {showDrawerOption && (
          <label className="flex items-start gap-2 cursor-pointer rounded-md border border-border p-3">
            <input
              type="checkbox"
              className="mt-0.5"
              checked={paidFromDrawer}
              onChange={(e) =>
                setValue("paidFromDrawer", e.target.checked, {
                  shouldValidate: true,
                })
              }
            />
            <span className="text-sm text-card-foreground">
              Payé depuis la caisse ?
              <span className="block text-xs text-muted-foreground">
                Génère une sortie de caisse liée. Une session de caisse doit être
                ouverte.
              </span>
            </span>
          </label>
        )}

        {noOpenSession && (
          <div className="flex items-start gap-2 rounded-md border border-amber-300 bg-amber-50 p-3 text-sm text-amber-800">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" />
            <span>
              Aucune session de caisse ouverte.{" "}
              <Link to="/register" className="font-medium underline">
                Ouvrir la caisse
              </Link>{" "}
              pour enregistrer une dépense payée depuis la caisse.
            </span>
          </div>
        )}

        <div className="flex justify-end gap-2 border-t border-border pt-4">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Annuler
          </Button>
          <Button type="submit" isLoading={isLoading} size="sm">
            Enregistrer la dépense
          </Button>
        </div>
      </form>
    </Modal>
  );
};

export default ExpenseFormModal;
